import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Chip from '../common/chip'
import { Payment } from '../../types'
import { numberWithCommas } from '../../utils/common.utils'
import TicketIcon from '../../assets/icons/ticket-icon.svg'
import CalendarIcon from '../../assets/icons/calendar-icon.svg'
import SimpleCheckIcon from '../../assets/icons/simple-check-icon.svg'
import PaymentTypeIcon from '../../assets/icons/payment-type-icon.svg'
import MonetizationIcon from '../../assets/icons/monetization-icon.svg'
import CustomerDetailsPaymentDetailsDataTableRow from './customer-details-payment-details-data-table-row'

const CustomerDetailsPaymentDetailsDataTable = ({ paymentDetailsData }: { paymentDetailsData: Partial<Payment> }) => {
  const { t } = useTranslation(['customer-details-management'])

  const [paymentCustomerFullName, setPaymentCustomerFullName] = useState<string>('')

  useEffect(() => {
    if (paymentDetailsData.customerRegisteredFullName) {
      setPaymentCustomerFullName(paymentDetailsData.customerRegisteredFullName.toUpperCase())
      return
    }

    if (paymentDetailsData.customerData) {
      setPaymentCustomerFullName(String(paymentDetailsData.customerData?.registeredFullName))
    } else {
      setPaymentCustomerFullName('NOT IDENTIFIED')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentDetailsData])

  return (
    <div className="customer-details-payment-details-data-table-container">
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={TicketIcon}
            alt="ticket-icon"
          />
        }
        rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.beneficiaryName')}
        rowValue={
          paymentCustomerFullName ? (
            paymentCustomerFullName
          ) : (
            <div
              className="data-container-loader"
              style={{ padding: '12px', width: '90%', maxWidth: '200px', alignSelf: 'flex-start' }}
            ></div>
          )
        }
      />
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={TicketIcon}
            alt="ticket-icon"
          />
        }
        rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.referenceNumber')}
        rowValue={paymentDetailsData?.referenceNumber ?? ''}
      />
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={CalendarIcon}
            alt="calendar-icon"
          />
        }
        rowName={paymentDetailsData.status === 'paid' ? t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.paymentDate') : t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.creationDate')}
        rowValue={
          paymentDetailsData.status === 'paid' && paymentDetailsData.paidAt
            ? format(paymentDetailsData.paidAt, 'dd/MM/yyyy | HH:mm:ss')
            : paymentDetailsData.createdAt
            ? format(paymentDetailsData.createdAt, 'dd/MM/yyyy | HH:mm:ss')
            : ''
        }
      />
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={MonetizationIcon}
            alt="monetization-icon"
          />
        }
        rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.paymentAmount')}
        rowValue={`${numberWithCommas(Number(paymentDetailsData.amount))} ${paymentDetailsData.currency?.toUpperCase() ?? 'COP'}`}
      />
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={PaymentTypeIcon}
            alt="payment-type-icon"
          />
        }
        rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.paymentType')}
        rowValue={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsValues.withdraw')}
      />
      {paymentDetailsData.additionalValues && (
        <CustomerDetailsPaymentDetailsDataTableRow
          icon={
            <img
              src={TicketIcon}
              alt="ticket-icon"
            />
          }
          rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.paxumId')}
          rowValue={String(paymentDetailsData.additionalValues[0].value)}
        />
      )}
      {paymentDetailsData.description && (
        <div
          className="customer-details-payment-details-description-row-container"
          style={{ width: '100%' }}
        >
          <div className="customer-details-payment-details-description-row-title">{t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.paymentDescription')}</div>
          <div className="customer-details-payment-details-description-row-text">{paymentDetailsData.description}</div>
        </div>
      )}
      <CustomerDetailsPaymentDetailsDataTableRow
        icon={
          <img
            src={SimpleCheckIcon}
            alt="simple-check-icon"
          />
        }
        lastValue
        rowName={t('paymentDetailsModal.detailsPaymentDetailsDataTable.rowsNames.status')}
        rowValue={
          <Chip
            status={paymentDetailsData.status ?? 'reviewed'}
            label={
              paymentDetailsData.status === 'reviewed'
                ? t('paymentDetailsModal.chipStatusTexts.reviewed')
                : paymentDetailsData.status === 'paid'
                ? t('paymentDetailsModal.chipStatusTexts.paid')
                : paymentDetailsData.status === 'processing'
                ? t('paymentDetailsModal.chipStatusTexts.processing')
                : paymentDetailsData.status === 'error'
                ? t('paymentDetailsModal.chipStatusTexts.error')
                : paymentDetailsData.status === 'validated'
                ? t('paymentDetailsModal.chipStatusTexts.validated')
                : paymentDetailsData.status === 'rejected'
                ? t('paymentDetailsModal.chipStatusTexts.rejected')
                : paymentDetailsData.status === 'returned'
                ? t('paymentDetailsModal.chipStatusTexts.returned')
                : paymentDetailsData.status === 'waiting-for-resolution'
                ? t('paymentDetailsModal.chipStatusTexts.waitingForResolution')
                : t('paymentDetailsModal.chipStatusTexts.forReview')
            }
          />
        }
      />
      {paymentDetailsData.status === 'reviewed' && paymentDetailsData.documentId !== '' && <div style={{ color: '#F59E0B', fontWeight: '500' }}>{t('paymentDetailsModal.sendWarning')}</div>}
    </div>
  )
}

export default CustomerDetailsPaymentDetailsDataTable
